import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  listMyWebhooks,
  listMyWebhookEvents,
  createMyWebhook,
  updateMyWebhook,
  deleteMyWebhook,
  getMyWebhookSecret,
  listMyWebhookDeliveries,
} from '../../services/api';
import { formatDateTime } from '../../utils/formatDateTime';

/**
 * TenantWebhooks — UI de gerenciamento de webhooks do tenant.
 *
 * Feature Enterprise `webhooks`. Permite cadastrar URLs que recebem
 * notificações HTTP POST quando eventos acontecem (appointment.created,
 * appointment.updated, etc) com signature HMAC-SHA256.
 *
 * Estrutura da página:
 * - Aba "Configurações": lista de webhook_configs + CRUD
 * - Aba "Entregas": log das últimas 100 deliveries com status
 */
export default function TenantWebhooks() {
  const confirm = useConfirm();
  const [tab, setTab] = useState('configs'); // 'configs' | 'deliveries'
  const [configs, setConfigs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [supportedEvents, setSupportedEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);       // config sendo editada (null = novo)
  const [newlyCreated, setNewlyCreated] = useState(null); // { id, name, secret, ... } após criar
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', events: [], active: true });
  const [saving, setSaving] = useState(false);

  const [secretVisible, setSecretVisible] = useState(null); // { configId, secret }
  const [statusFilter, setStatusFilter] = useState('');

  const loadConfigs = () => {
    setLoading(true);
    Promise.all([
      listMyWebhooks().then(r => r.data.webhooks || []),
      listMyWebhookEvents().then(r => r.data.events || []),
    ])
      .then(([cfgs, evts]) => {
        setConfigs(cfgs);
        setSupportedEvents(evts);
      })
      .catch(err => toast.error(err.response?.data?.error || 'Erro ao carregar webhooks'))
      .finally(() => setLoading(false));
  };

  const loadDeliveries = () => {
    setLoading(true);
    listMyWebhookDeliveries({ status: statusFilter || undefined, limit: 100 })
      .then(r => setDeliveries(r.data.deliveries || []))
      .catch(err => toast.error(err.response?.data?.error || 'Erro ao carregar entregas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (tab === 'configs') loadConfigs();
    else loadDeliveries();
  }, [tab, statusFilter]);

  // ── Modal: novo / editar ────────────────────────────────────────────────
  const openNew = () => {
    setEditing(null);
    setNewlyCreated(null);
    setCopied(false);
    setForm({ name: '', url: '', events: [], active: true });
    setModalOpen(true);
  };

  const openEdit = cfg => {
    setEditing(cfg);
    setNewlyCreated(null);
    setCopied(false);
    setForm({
      name: cfg.name,
      url: cfg.url,
      events: cfg.events || [],
      active: cfg.active !== false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setNewlyCreated(null);
    setCopied(false);
  };

  const toggleEvent = evt => {
    setForm(f => ({
      ...f,
      events: f.events.includes(evt)
        ? f.events.filter(e => e !== evt)
        : [...f.events, evt],
    }));
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('Nome precisa ter pelo menos 2 caracteres');
      return;
    }
    if (!form.url || !/^https?:\/\//i.test(form.url)) {
      toast.error('URL precisa começar com http:// ou https://');
      return;
    }
    if (form.events.length === 0) {
      toast.error('Selecione pelo menos 1 evento para assinar');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await updateMyWebhook(editing.id, {
          name: form.name.trim(),
          url: form.url,
          events: form.events,
          active: form.active,
        });
        toast.success('Webhook atualizado');
        closeModal();
        loadConfigs();
      } else {
        const res = await createMyWebhook({
          name: form.name.trim(),
          url: form.url,
          events: form.events,
          active: form.active,
        });
        setNewlyCreated(res.data.webhook);
        loadConfigs();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const secret = newlyCreated?.secret || secretVisible?.secret;
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Não foi possível copiar — selecione o texto manualmente');
    }
  };

  const handleViewSecret = async cfg => {
    const ok = await confirm({
      title: 'Reconsultar secret?',
      message: 'O secret será exibido para você copiar novamente. Essa ação é auditada.',
      confirmText: 'Mostrar secret',
      cancelText: 'Cancelar',
      variant: 'warning',
    });
    if (!ok) return;
    try {
      const res = await getMyWebhookSecret(cfg.id);
      setSecretVisible({ configId: cfg.id, configName: cfg.name, secret: res.data.secret });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao reconsultar secret');
    }
  };

  const handleDelete = async cfg => {
    const ok = await confirm({
      title: `Excluir webhook "${cfg.name}"?`,
      message: `A URL ${cfg.url} parará de receber notificações imediatamente. Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Voltar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMyWebhook(cfg.id);
      toast.success('Webhook excluído');
      loadConfigs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  // ── Helpers de formatação ───────────────────────────────────────────────
  const fmtDate = formatDateTime;

  const statusBadge = s => {
    const map = {
      pending:   { label: 'Pendente',  cls: 'badge-info' },
      retrying:  { label: 'Tentando',  cls: 'badge-warning' },
      delivered: { label: 'Entregue',  cls: 'badge-success' },
      failed:    { label: 'Falhou',    cls: 'badge-danger' },
    };
    return map[s] || { label: s, cls: 'badge-gray' };
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>🔔 Webhooks</h1>
          <p className="page-subtitle">Configure URLs que vão receber notificações HTTP quando eventos do seu negócio acontecerem.</p>
        </div>
        {tab === 'configs' && (
          <button className="btn btn-primary" onClick={openNew}>+ Novo webhook</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--gray-100)', marginBottom: 20 }}>
        {[['configs', '⚙️ Configurações'], ['deliveries', '📜 Entregas']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setTab(val)}
            style={{
              padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              color: tab === val ? 'var(--primary)' : 'var(--gray-500)',
              borderBottom: tab === val ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -2,
            }}
          >{label}</button>
        ))}
      </div>

      {tab === 'configs' && (
        <>
          <div className="info-box" style={{ marginBottom: 20, fontSize: 13 }}>
            📖 <strong>Como funciona:</strong> quando um evento assinado acontece, o AtendoApp faz um POST HTTP com o payload pro URL configurado. A request inclui o header <code>X-AtendoApp-Signature: sha256=&lt;hex&gt;</code> — verifique esse header no seu servidor computando HMAC-SHA256 do body com o secret. Retry automático até 3 tentativas em caso de falha.
          </div>

          {loading ? (
            <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
          ) : configs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔔</div>
              <h3>Nenhum webhook configurado</h3>
              <p>Clique em "Novo webhook" para criar o primeiro.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div className="card-body" style={{ padding: 0 }}>
                {configs.map(cfg => (
                  <div key={cfg.id} style={{
                    padding: '14px 18px', borderBottom: '1px solid var(--gray-100)',
                    opacity: cfg.active ? 1 : 0.55,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {cfg.name}
                          {!cfg.active && <span className="badge badge-gray" style={{ fontSize: 10 }}>Pausado</span>}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 3, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {cfg.url}
                        </div>
                        <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(cfg.events || []).map(evt => (
                            <span key={evt} className="badge badge-info" style={{ fontSize: 9, fontFamily: 'monospace' }}>{evt}</span>
                          ))}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                          Criado {fmtDate(cfg.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => handleViewSecret(cfg)}>🔐 Secret</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(cfg)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(cfg)}>Excluir</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'deliveries' && (
        <>
          <div className="filter-bar" style={{ marginBottom: 14 }}>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Todos status</option>
              <option value="pending">Pendentes</option>
              <option value="retrying">Tentando</option>
              <option value="delivered">Entregues</option>
              <option value="failed">Falharam</option>
            </select>
            <button className="btn btn-ghost btn-sm" onClick={loadDeliveries}>⟳ Atualizar</button>
          </div>
          {loading ? (
            <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
          ) : deliveries.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>Nenhuma entrega registrada</h3>
              <p>Entregas aparecem aqui quando eventos do seu negócio disparam webhooks.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div className="card-body" style={{ padding: 0 }}>
                {deliveries.map(d => {
                  const sb = statusBadge(d.status);
                  return (
                    <div key={d.id} style={{ padding: '12px 18px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span className={`badge ${sb.cls}`} style={{ fontSize: 10, minWidth: 70, textAlign: 'center' }}>{sb.label}</span>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{d.event}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                          #{d.id} · {fmtDate(d.created_at)}
                          {d.attempt_count > 0 && ` · ${d.attempt_count} tentativa${d.attempt_count > 1 ? 's' : ''}`}
                          {d.response_status && ` · HTTP ${d.response_status}`}
                        </div>
                        {d.last_error && (
                          <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 3, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {d.last_error}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Modal create/edit ──────────────────────────────────────────── */}
      {modalOpen && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3>
                {newlyCreated ? '✅ Webhook criado' : editing ? 'Editar webhook' : 'Novo webhook'}
              </h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            {!newlyCreated ? (
              <form onSubmit={handleSave}>
                <div className="modal-body" style={{ maxHeight: 500, overflowY: 'auto' }}>
                  <div className="form-group">
                    <label>Nome *</label>
                    <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: CRM externo" autoFocus maxLength={100} />
                  </div>
                  <div className="form-group">
                    <label>URL do seu servidor *</label>
                    <input type="url" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://seu-servidor.com/webhooks/atendoapp" maxLength={500} />
                    <small>Deve responder 2xx em até 10 segundos. Use HTTPS em produção.</small>
                  </div>
                  <div className="form-group">
                    <label>Eventos a assinar *</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                      {supportedEvents.map(evt => (
                        <label key={evt} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer', background: form.events.includes(evt) ? 'var(--gray-50)' : 'transparent' }}>
                          <input type="checkbox" checked={form.events.includes(evt)} onChange={() => toggleEvent(evt)} />
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{evt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                      <span>Ativo (recebe eventos)</span>
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancelar</button>
                  <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>
                    {saving ? 'Salvando…' : editing ? 'Salvar' : 'Criar webhook'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="modal-body">
                  <div className="highlight-box" style={{ marginBottom: 16, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                    ⚠️ <strong>Copie o secret agora!</strong> Use esse valor pra verificar a signature HMAC do lado do seu servidor. Você ainda vai poder reconsultá-lo no botão "🔐 Secret" na lista, mas recomendamos guardar num gerenciador de secrets.
                  </div>
                  <div className="form-group">
                    <label>Secret HMAC</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input type="text" value={newlyCreated.secret} readOnly style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--gray-50)', flex: 1 }} onFocus={e => e.target.select()} />
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleCopy}>
                        {copied ? '✓ Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                  <div className="form-group" style={{ fontSize: 12, color: 'var(--gray-600)' }}>
                    <strong>Verificação no seu servidor (exemplo Node.js):</strong>
                    <pre style={{ background: 'var(--gray-50)', padding: 10, borderRadius: 6, fontSize: 11, marginTop: 6, overflow: 'auto' }}>
{`const crypto = require('crypto');
const expected = 'sha256=' + crypto
  .createHmac('sha256', '${newlyCreated.secret}')
  .update(rawBody)
  .digest('hex');
if (req.headers['x-atendoapp-signature'] !== expected) {
  return res.status(401).send('Invalid signature');
}`}
                    </pre>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={closeModal}>Entendi, fechar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modal reconsulta de secret ──────────────────────────────────── */}
      {secretVisible && (
        <div className="modal-backdrop" onClick={() => setSecretVisible(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>🔐 Secret — {secretVisible.configName}</h3>
              <button className="modal-close" onClick={() => setSecretVisible(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Secret HMAC</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="text" value={secretVisible.secret} readOnly style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--gray-50)', flex: 1 }} onFocus={e => e.target.select()} />
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleCopy}>
                    {copied ? '✓ Copiado' : 'Copiar'}
                  </button>
                </div>
                <small style={{ color: 'var(--gray-500)' }}>Esta consulta foi auditada nos logs do sistema.</small>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setSecretVisible(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
